import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { supabase } from "@/integrations/supabase/client";
import { appleStores } from "@/data/appleStores";
import { MapPin, Loader2 } from "lucide-react";

const MiniMapbox = () => {
  const navigate = useNavigate();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [mapboxToken, setMapboxToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);

  // Fetch Mapbox token
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const { data, error } = await supabase.functions.invoke("get-mapbox-token");
        if (error) throw error;
        setMapboxToken(data.token);
      } catch (err) {
        console.error("Error fetching Mapbox token:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchToken();
  }, []);

  // Get user location
  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords: [number, number] = [position.coords.longitude, position.coords.latitude];
          setUserLocation(coords);
        },
        (error) => {
          console.error("Geolocation error:", error);
          // Default to Europe center if geolocation fails
          setUserLocation([12.4964, 41.9028]); // Rome, Italy
        },
        { enableHighAccuracy: true, timeout: 5000 }
      );
    } else {
      setUserLocation([12.4964, 41.9028]);
    }
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || !mapboxToken || !userLocation) return;

    mapboxgl.accessToken = mapboxToken;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center: userLocation,
      zoom: 10,
      interactive: false, // Disable all interactions for mini map
      attributionControl: false,
    });

    map.current.on("load", () => {
      // Add user location marker
      const userEl = document.createElement("div");
      userEl.innerHTML = `
        <div class="relative">
          <div class="w-4 h-4 bg-blue-500 rounded-full border-2 border-white shadow-lg"></div>
          <div class="absolute inset-0 w-4 h-4 bg-blue-500 rounded-full animate-ping opacity-30"></div>
        </div>
      `;
      new mapboxgl.Marker(userEl)
        .setLngLat(userLocation)
        .addTo(map.current!);

      // Find and add nearby Apple Store markers
      const nearbyStores = appleStores.filter((store) => {
        const [lng, lat] = store.coordinates;
        const distance = Math.sqrt(
          Math.pow(lng - userLocation[0], 2) + Math.pow(lat - userLocation[1], 2)
        );
        return distance < 10; // Rough distance filter (about 1000km)
      }).slice(0, 10); // Show max 10 stores

      nearbyStores.forEach((store) => {
        const el = document.createElement("div");
        el.innerHTML = `
          <div class="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg border border-gray-200">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-black">
              <path d="M12 20.94c1.5 0 2.75 1.06 4 1.06 3 0 6-8 6-12.22A4.91 4.91 0 0 0 17 5c-2.22 0-4 1.44-5 2-1-.56-2.78-2-5-2a4.9 4.9 0 0 0-5 4.78C2 14 5 22 8 22c1.25 0 2.5-1.06 4-1.06Z"/>
              <path d="M10 2c1 .5 2 2 2 5"/>
            </svg>
          </div>
        `;
        new mapboxgl.Marker(el)
          .setLngLat(store.coordinates as [number, number])
          .addTo(map.current!);
      });
    });

    return () => {
      map.current?.remove();
    };
  }, [mapboxToken, userLocation]);

  const handleClick = () => {
    // Store location before navigating
    sessionStorage.setItem('userLocation', JSON.stringify(userLocation));
    
    // Navigate immediately - let AppleStore handle the animation
    navigate("/apple-store", { state: { fromMiniMap: true, userLocation } });
  };

  if (isLoading) {
    return (
      <div className="relative rounded-xl overflow-hidden shadow-lg border border-border h-[400px] bg-muted/30 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Caricamento mappa...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      ref={containerRef}
      className="relative rounded-xl overflow-hidden shadow-lg border border-border cursor-pointer group will-change-transform"
      onClick={handleClick}
      whileHover={{ scale: 1.015 }}
      whileTap={{ scale: 0.985 }}
      transition={{ 
        type: "spring", 
        stiffness: 400, 
        damping: 30 
      }}
    >
      <div 
        ref={mapContainer} 
        className="w-full h-[400px]"
      />
      
      {/* Overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent pointer-events-none" />
      
      {/* Click indicator with pulse animation */}
      <motion.div 
        className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 bg-background/90 backdrop-blur-sm rounded-full shadow-lg"
        animate={{ 
          y: [0, -4, 0],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        <MapPin className="w-4 h-4 text-primary" />
        <span className="text-sm font-medium text-foreground">Tocca per esplorare</span>
      </motion.div>

      {/* Ripple effect on hover */}
      <motion.div 
        className="absolute inset-0 bg-primary/10 pointer-events-none"
        initial={{ opacity: 0 }}
        whileHover={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
      />
    </motion.div>
  );
};

export default MiniMapbox;
